import React, { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import { Slot } from '../../../app/Subscriptions/types/SubscriptionType';

interface AttendanceDurationDialogProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (duration: number, comments: string, slotName?: string, isMarkUp?: boolean, present?: boolean) => void;
    initialDuration?: number;
    initialComments?: string;
    initialSlot?: string;
    slots?: Slot[];
    hasSlots?: boolean;
    isCountHourly?: boolean;
    loading?: boolean;
    currentStatus?: 'present' | 'absent' | undefined;
    initialMakeupStatus?: boolean;
    userName?: string;
    selectedDay?: string;
}

export default function AttendanceDurationDialog({
    open,
    onClose,
    onSubmit,
    initialDuration = 1,
    initialComments = "",
    initialSlot = "",
    slots = [],
    hasSlots = false,
    isCountHourly = false,
    loading = false,
    currentStatus,
    initialMakeupStatus = false,
    userName,
    selectedDay
}: AttendanceDurationDialogProps) {
    const [duration, setDuration] = useState(initialDuration);
    const [comments, setComments] = useState(initialComments);
    const [selectedSlot, setSelectedSlot] = useState<string>(initialSlot);
    const [isMarkup, setIsMarkup] = useState(false);
    const [present, setPresent] = useState<string>(currentStatus || 'present');



    // Sync makeup status with prop when dialog opens
    useEffect(() => {
        if (open) {

            setDuration(initialDuration);
            setComments(initialComments);
            setSelectedSlot(initialSlot);
            // Show current status exactly as it is
            setPresent(currentStatus || 'present');
            // Set makeup status based on initialMakeupStatus
            setIsMarkup(Boolean(initialMakeupStatus));

        }
    }, [open, initialDuration, initialComments, initialSlot, currentStatus, initialMakeupStatus, userName, selectedDay, isCountHourly]);

    // Disable makeup when absent is selected (but only when user changes it, not during initialization)
    useEffect(() => {
        if (present === 'absent' && open) {
            setIsMarkup(false);
        }
    }, [present, open]);

    // Ensure makeup status is synced with prop
    useEffect(() => {
        if (open) {
            setIsMarkup(Boolean(initialMakeupStatus));
            console.log('Syncing makeup status to:', Boolean(initialMakeupStatus));
        }
    }, [initialMakeupStatus, open]);

    const handleSubmit = () => {
        // For non-count hourly subscriptions, always send duration as 1
        const finalDuration = isCountHourly ? duration : 1;
        console.log('Submitting attendance with values:', {
            duration: finalDuration,
            comments,
            slotName: selectedSlot || undefined,
            isMarkUp: isMarkup,
            present: present === 'present'
        });
        onSubmit(finalDuration, comments, selectedSlot || undefined, isMarkup, present === 'present');
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: '12px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }
            }}
        >
            <DialogTitle 
                sx={{ 
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '1.25rem',
                    padding: '20px 24px',
                    borderTopLeftRadius: '12px',
                    borderTopRightRadius: '12px'
                }}
            >
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-white bg-opacity-20 rounded-full">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                    </div>
                    {!isCountHourly && currentStatus ? 
                        `Mark Attendance for ${userName || 'Student'} - Day ${selectedDay || 'Unknown'} (Currently ${currentStatus === 'present' ? (initialMakeupStatus ? 'Present (Makeup)' : 'Present') : 'Absent'})` : 
                        `Mark Attendance for ${userName || 'Student'} - Day ${selectedDay || 'Unknown'}`
                    }
                </div>
            </DialogTitle>
            
            <DialogContent sx={{ padding: '24px' }}>
                <div className="space-y-6">
                    {/* Only show duration input for count hourly subscriptions */}
                    {isCountHourly && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Duration (hours)</label>
                            <TextField
                                type="number"
                                value={duration}
                                onChange={(e) => setDuration(Number(e.target.value))}
                                fullWidth
                                inputProps={{ step: 0.5 }}
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '&:hover fieldset': {
                                            borderColor: '#667eea',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#667eea',
                                        },
                                    },
                                }}
                            />
                        </div>
                    )}
                    
                    {/* Show slot selection only if subscription has slots */}
                    {hasSlots && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Select Slot</label>
                            <TextField
                                select
                                value={selectedSlot}
                                onChange={(e) => setSelectedSlot(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '&:hover fieldset': {
                                            borderColor: '#667eea',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#667eea',
                                        },
                                    },
                                }}
                            >
                                {slots.map((slot) => (
                                    <MenuItem key={slot.id} value={slot.name}>
                                        <div className="flex items-center gap-2">
                                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                            {slot.name} ({slot.dayOfWeek.join(', ')} at {slot.startTime})
                                        </div>
                                    </MenuItem>
                                ))}
                            </TextField>
                        </div>
                    )}

                    {/* Show Present/Absent selection for non-count hourly subscriptions */}
                    {!isCountHourly && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium text-gray-700">Attendance Status</label>
                            <TextField
                                select
                                value={present}
                                onChange={(e) => setPresent(e.target.value)}
                                fullWidth
                                required
                                variant="outlined"
                                size="small"
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '8px',
                                        '&:hover fieldset': {
                                            borderColor: '#667eea',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#667eea',
                                        },
                                    },
                                }}
                            >
                                <MenuItem value="present">Present</MenuItem>
                                <MenuItem value="absent">Absent</MenuItem>
                            </TextField>
                        </div>
                    )}

                    {/* Show makeup toggle for all subscription types */}
                    <div className="space-y-2">
                        {/* Warning for slots subscriptions */}
                        {hasSlots && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3">
                                <div className="flex items-start gap-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <div className="text-sm">
                                        <div className="font-medium text-amber-800 mb-1">Makeup Classes Across Slots</div>
                                        <div className="text-amber-700">
                                            Makeup classes can be marked across different time slots. Use this feature wisely to maintain accurate attendance records.
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={isMarkup}
                                    onChange={(e) => {
                                        console.log('Switch changed from', isMarkup, 'to', e.target.checked);
                                        setIsMarkup(e.target.checked);
                                    }}
                                    disabled={!isCountHourly && present === 'absent'}
                                    sx={{
                                        '& .MuiSwitch-switchBase.Mui-checked': {
                                            color: '#8b5cf6',
                                            '&:hover': {
                                                backgroundColor: 'rgba(139, 92, 246, 0.08)',
                                            },
                                        },
                                        '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                            backgroundColor: '#8b5cf6',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-disabled': {
                                            color: '#d1d5db',
                                        },
                                        '& .MuiSwitch-switchBase.Mui-disabled + .MuiSwitch-track': {
                                            backgroundColor: '#e5e7eb',
                                        },
                                    }}
                                />
                            }
                            label={
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                                    <span className={`text-sm font-medium ${!isCountHourly && present === 'absent' ? 'text-gray-400' : 'text-gray-700'}`}>
                                        Mark as Makeup Class {!isCountHourly && present === 'absent' && '(Not available for Absent)'}
                                    </span>
                                </div>
                            }
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Comments (Optional)</label>
                        <TextField
                            multiline
                            rows={3}
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            fullWidth
                            variant="outlined"
                            size="small"
                            placeholder="Add any additional notes..."
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    '&:hover fieldset': {
                                        borderColor: '#667eea',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#667eea',
                                    },
                                },
                            }}
                        />
                    </div>
                </div>
            </DialogContent>
            
            <DialogActions sx={{ padding: '16px 24px 24px', gap: '12px' }}>
                <Button 
                    onClick={handleClose} 
                    disabled={loading}
                    sx={{
                        color: '#6b7280',
                        '&:hover': {
                            backgroundColor: '#f3f4f6',
                        },
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                    }}
                >
                    Cancel
                </Button>
                <Button 
                    onClick={handleSubmit} 
                    variant="contained" 
                    disabled={loading || (hasSlots && !selectedSlot)}
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 500,
                        minWidth: '100px',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)',
                        },
                        '&:disabled': {
                            background: '#e5e7eb',
                            color: '#9ca3af',
                        },
                    }}
                    startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {loading ? 'Saving...' : 'Update Attendance'}
                </Button>
            </DialogActions>
        </Dialog>
    );
} 